import type { AdminOrgInfoBody, AdminServiceDeskInfo, OrganizationType } from '@/stores/store-interfaces';

// Maps supported default_system_role values to the locked service-desk request type defaults used by the org-creation flow.
type ServiceDeskRequestType = AdminServiceDeskInfo['request_types'][number] & {
    request_type_type?: string;
};
type OrganizationRoleFields = Pick<OrganizationType, 'default_system_role'>;

const CONTRACTOR_SERVICE_PORTAL_NAME = 'atNorth Service Portal';
const CONTRACTOR_VISIT_REQUEST_TYPE_NAME = 'Visit';
export const INCIDENT_SERVICE_DESK_ID = 309;
const INCIDENT_MANAGEMENT_SERVICE_DESK_NAME = 'Incident Management';
const INCIDENT_MANAGEMENT_NEW_SERVICE_DESK_NAME = 'Incident Management (New)';
export type LockedDefaultsRole = 'contractor' | 'customer';

// Contractor defaults currently approved for auto-selection.
export const CONTRACTOR_INCIDENT_REQUEST_TYPE_NAMES = [
    'Actionable Observation',
    'Positive Observation',
    'Waste management',
    'Fire / Explosion',
    'Chemical spill / emissions',
    'People Incident',
    'Legal / Regulatory',
    'Physical Security',
    'Service Quality & Contractual deviation',
] as const;

export const CUSTOMER_INCIDENT_REQUEST_TYPE_NAMES = [
    'Actionable Observation',
    'Positive Observation',
    'Waste management',
    'Fire / Explosion',
    'Chemical spill / emissions',
    'People Incident',
    'Customer satisfaction',
    'Service Quality & Contractual deviation',
    'Physical Security',
    'Network Infrastructure',
    'Facility Infrastructure',
    'Physical Infrastructure',
    'Cooling / HVAC',
    'Power & Electrical systems',
] as const;

function normalizeLockedDefaultsRole(value: null | string | undefined): LockedDefaultsRole | null {
    return value === 'contractor' || value === 'customer' ? value : null;
}

export function getLockedDefaultsRole(organizationType?: null | OrganizationRoleFields): LockedDefaultsRole | null {
    return normalizeLockedDefaultsRole(organizationType?.default_system_role);
}

export function isContractorOrganization(organizationType?: null | OrganizationRoleFields) {
    return getLockedDefaultsRole(organizationType) === 'contractor';
}

export function getLockedDefaultsRoleById(
    organizationTypeId: null | string | undefined,
    organizationTypes: OrganizationType[] = [],
): LockedDefaultsRole | null {
    if (!organizationTypeId) {
        return null;
    }

    const organizationType = organizationTypes.find((item) => item.organization_type_id === organizationTypeId);
    return getLockedDefaultsRole(organizationType);
}

function hasIncidentRequestTypes(serviceDesk: AdminServiceDeskInfo) {
    return serviceDesk.request_types.some((requestType) => {
        const incidentRequestType = requestType as ServiceDeskRequestType;
        return incidentRequestType.request_type_type === 'incident';
    });
}

function findServicePortal(serviceDesks: AdminServiceDeskInfo[]) {
    return serviceDesks.find((serviceDesk) => serviceDesk.name === CONTRACTOR_SERVICE_PORTAL_NAME);
}

function buildServicePortalVisitDefault(serviceDesks: AdminServiceDeskInfo[]): AdminOrgInfoBody['service_desks'] {
    const servicePortal = findServicePortal(serviceDesks);
    const visitRequestType = servicePortal?.request_types.find(
        (requestType) => requestType.name === CONTRACTOR_VISIT_REQUEST_TYPE_NAME,
    );

    if (servicePortal && visitRequestType) {
        return [
            {
                service_desk_id: servicePortal.service_desk_id,
                request_types: [{ request_type_id: visitRequestType.request_type_id }],
            },
        ];
    }

    return [];
}

function getApprovedIncidentRequestTypes(
    serviceDesk: AdminServiceDeskInfo,
    approvedIncidentRequestTypeNameSet: ReadonlySet<string>,
) {
    return serviceDesk.request_types.filter((requestType) => approvedIncidentRequestTypeNameSet.has(requestType.name));
}

function getSupportRequestTypes(serviceDesk: AdminServiceDeskInfo) {
    return serviceDesk.request_types.filter((requestType) => {
        const typedRequestType = requestType as ServiceDeskRequestType;
        return typedRequestType.request_type_type !== 'incident';
    });
}

function findIncidentManagementServiceDesk(
    serviceDesks: AdminServiceDeskInfo[],
    approvedIncidentRequestTypeNameSet: ReadonlySet<string>,
) {
    const candidateServiceDesks = [
        ...serviceDesks.filter((serviceDesk) => serviceDesk.service_desk_id === INCIDENT_SERVICE_DESK_ID),
        ...serviceDesks.filter((serviceDesk) => serviceDesk.name === INCIDENT_MANAGEMENT_SERVICE_DESK_NAME),
        ...serviceDesks.filter(
            (serviceDesk) =>
                serviceDesk.service_desk_id !== INCIDENT_SERVICE_DESK_ID &&
                serviceDesk.name !== INCIDENT_MANAGEMENT_SERVICE_DESK_NAME &&
                serviceDesk.name !== INCIDENT_MANAGEMENT_NEW_SERVICE_DESK_NAME &&
                hasIncidentRequestTypes(serviceDesk),
        ),
    ];

    return candidateServiceDesks.find(
        (serviceDesk) => getApprovedIncidentRequestTypes(serviceDesk, approvedIncidentRequestTypeNameSet).length > 0,
    );
}

export function buildLockedIncidentServiceDeskDefaultsForOrganizationType(
    kind: LockedDefaultsRole,
    serviceDesks: AdminServiceDeskInfo[],
): AdminOrgInfoBody['service_desks'] {
    const approvedIncidentRequestTypeNames =
        kind === 'contractor' ? CONTRACTOR_INCIDENT_REQUEST_TYPE_NAMES : CUSTOMER_INCIDENT_REQUEST_TYPE_NAMES;
    const approvedIncidentRequestTypeNameSet = new Set<string>(approvedIncidentRequestTypeNames);
    const incidentManagementServiceDesk = findIncidentManagementServiceDesk(
        serviceDesks,
        approvedIncidentRequestTypeNameSet,
    );
    const approvedIncidentRequestTypes = incidentManagementServiceDesk
        ? getApprovedIncidentRequestTypes(incidentManagementServiceDesk, approvedIncidentRequestTypeNameSet)
        : undefined;

    if (incidentManagementServiceDesk && approvedIncidentRequestTypes && approvedIncidentRequestTypes.length > 0) {
        return [
            {
                service_desk_id: incidentManagementServiceDesk.service_desk_id,
                request_types: approvedIncidentRequestTypes.map((requestType) => ({
                    request_type_id: requestType.request_type_id,
                })),
            },
        ];
    }

    return [];
}

export function buildLockedServiceDeskDefaultsForOrganizationType(
    kind: LockedDefaultsRole,
    serviceDesks: AdminServiceDeskInfo[],
): AdminOrgInfoBody['service_desks'] {
    if (kind === 'contractor') {
        return mergeServiceDeskSelections(
            buildServicePortalVisitDefault(serviceDesks),
            buildLockedIncidentServiceDeskDefaultsForOrganizationType(kind, serviceDesks),
        );
    }

    return buildLockedIncidentServiceDeskDefaultsForOrganizationType(kind, serviceDesks);
}

export function buildEditableServiceDeskDefaultsForOrganizationType(
    kind: LockedDefaultsRole,
    serviceDesks: AdminServiceDeskInfo[],
): AdminOrgInfoBody['service_desks'] {
    if (kind === 'contractor') {
        return buildServicePortalVisitDefault(serviceDesks);
    }

    if (kind === 'customer') {
        const servicePortal = findServicePortal(serviceDesks);
        const supportRequestTypes = servicePortal ? getSupportRequestTypes(servicePortal) : [];

        if (servicePortal && supportRequestTypes.length > 0) {
            return [
                {
                    service_desk_id: servicePortal.service_desk_id,
                    request_types: supportRequestTypes.map((requestType) => ({
                        request_type_id: requestType.request_type_id,
                    })),
                },
            ];
        }

        return [];
    }

    return [];
}

export function mergeServiceDeskSelections(
    ...serviceDeskSelections: AdminOrgInfoBody['service_desks'][]
): AdminOrgInfoBody['service_desks'] {
    const mergedServiceDesks = new Map<number, Set<number>>();

    serviceDeskSelections.flat().forEach((serviceDesk) => {
        const requestTypes = mergedServiceDesks.get(serviceDesk.service_desk_id) ?? new Set<number>();

        serviceDesk.request_types.forEach((requestType) => {
            requestTypes.add(requestType.request_type_id);
        });

        mergedServiceDesks.set(serviceDesk.service_desk_id, requestTypes);
    });

    return Array.from(mergedServiceDesks.entries()).map(([service_desk_id, requestTypeIds]) => ({
        service_desk_id,
        request_types: Array.from(requestTypeIds).map((request_type_id) => ({
            request_type_id,
        })),
    }));
}
